import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { wsChannel } from '@xdocs/schemas';
import type { ErrorAlert, LinkResolveResult } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseLinkResolveResult {
  view: LinkResolveResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/**
 * `link.resolve` (F63) — the `/s/:token` capability-URL open. Anonymous: no
 * account needed, NOT moderation-gated for access (only `share.publish`'s
 * `public` path is — §15.3). Same reader shape as `discover.get`
 * (`sharePublicView`), so both feed `PublicShareReader`.
 *
 * Deliberately WORKS while logged out — see this dir's `WIRING.md` for the
 * cross-cutting WS-anonymous-connect gap this still needs from the auth/WS
 * owner to actually reach `connected` pre-login.
 */
export function useLinkResolve(token: string): UseLinkResolveResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && token.length > 0;

  const query = useQuery<LinkResolveResult, ErrorAlert>({
    queryKey: ['link-resolve', token],
    queryFn: () => requestTyped(wsClient, 'link.resolve', { token }),
    enabled,
    retry: false,
  });

  useEffect(() => {
    if (!query.data) return;
    const channel = wsChannel.share(query.data.share.id);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['link-resolve', token] });
    });
  }, [wsClient, queryClient, token, query.data]);

  return {
    view: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  };
}
